import React, { memo } from "react";
import { makeStyles } from '@material-ui/core';
import './styles.css';

const useStyles = makeStyles((theme) => ({
  pageLoaderContainer: {
    padding: theme.spacing(3),
    backgroundColor: 'var(--bg-default)',
    minHeight: 'calc(100vh - 64px)',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    color: 'var(--text-primary)',
  },
  skeletonHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
  },
  skeletonContent: {
    backgroundColor: 'var(--bg-paper)',
    borderRadius: theme.spacing(1),
    padding: theme.spacing(3),
    border: '1px solid var(--color-divider)',
    flex: 1,
  },
  skeletonToolbar: {
    display: 'flex',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(3),
  },
  skeletonList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  skeletonListItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: 'var(--bg-surface-2)',
    borderRadius: theme.spacing(0.75),
  },
  skeletonAvatar: {
    width: 40,
    height: 40,
    borderRadius: '50%',
    backgroundColor: 'var(--color-divider)',
    opacity: 0.3,
  },
  skeletonText: {
    backgroundColor: 'var(--color-divider)',
    borderRadius: theme.spacing(0.375),
    opacity: 0.4,
  },
  skeletonButton: {
    backgroundColor: 'var(--color-divider)',
    borderRadius: theme.spacing(0.5),
    opacity: 0.3,
  },
  skeletonChip: {
    backgroundColor: 'var(--color-divider)',
    borderRadius: 999,
    opacity: 0.3,
  },
  simpleLoaderContainer: {
    padding: theme.spacing(2.5),
    backgroundColor: 'var(--bg-default)',
    minHeight: 'calc(100vh - 64px)',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    color: 'var(--text-primary)',
  },
  simpleSkeletonContent: {
    backgroundColor: 'var(--bg-paper)',
    borderRadius: theme.spacing(1),
    padding: theme.spacing(2.5),
    border: '1px solid var(--color-divider)',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  contentOnlyContainer: {
    backgroundColor: 'var(--bg-default)',
    minHeight: '100%',
  },
  dashboardGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  dashboardCard: {
    backgroundColor: 'var(--bg-surface-2)',
    borderRadius: theme.spacing(1),
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.25),
  },
  dashboardChartRow: {
    display: 'grid',
    gridTemplateColumns: '1.4fr 1fr',
    gap: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
  dashboardChartCard: {
    backgroundColor: 'var(--bg-surface-2)',
    borderRadius: theme.spacing(1),
    padding: theme.spacing(2.5),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  chartBars: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: theme.spacing(1),
    height: 220,
    paddingTop: theme.spacing(2),
  },
  chartBar: {
    flex: 1,
    borderRadius: theme.spacing(0.5),
    backgroundColor: 'var(--color-divider)',
    opacity: 0.35,
  },
  listRowsDense: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  listRowDense: {
    display: 'grid',
    gridTemplateColumns: '42px 1.2fr 1fr 110px 86px',
    gap: theme.spacing(1.5),
    alignItems: 'center',
    padding: theme.spacing(1.5),
    backgroundColor: 'var(--bg-surface-2)',
    borderRadius: theme.spacing(0.75),
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '42px 1fr',
    },
  },
  editorShell: {
    backgroundColor: 'var(--bg-paper)',
    borderRadius: theme.spacing(1),
    border: '1px solid var(--color-divider)',
    display: 'grid',
    gridTemplateColumns: '280px 1fr',
    minHeight: '72vh',
    overflow: 'hidden',
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: '1fr',
    },
  },
  editorSidebar: {
    padding: theme.spacing(2),
    borderRight: '1px solid var(--color-divider)',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.25),
    [theme.breakpoints.down('md')]: {
      borderRight: 'none',
      borderBottom: '1px solid var(--color-divider)',
    },
  },
  editorCanvas: {
    position: 'relative',
    padding: theme.spacing(2.5),
    background: 'linear-gradient(180deg, var(--bg-surface-2) 0%, var(--bg-paper) 100%)',
  },
  editorNode: {
    position: 'absolute',
    width: 180,
    height: 82,
    borderRadius: theme.spacing(1),
    backgroundColor: 'var(--bg-paper)',
    border: '1px solid var(--color-divider)',
    boxShadow: '0 8px 24px rgba(0,0,0,0.06)',
    padding: theme.spacing(1.5),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  formSection: {
    backgroundColor: 'var(--bg-paper)',
    borderRadius: theme.spacing(1),
    border: '1px solid var(--color-divider)',
    padding: theme.spacing(2.5),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  formFieldGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
  loadingText: {
    color: 'var(--text-secondary)',
    fontSize: '0.875rem',
    opacity: 0.8,
    paddingLeft: theme.spacing(0.5),
  },
}));

// Skeleton loader específico para conteúdo das páginas - carregamento direto sem delays
const PageContentLoader = memo(({ variant = 'detailed', message = 'Carregando...' }) => {
  const classes = useStyles();

  if (variant === 'form') {
    return (
      <div className={classes.pageLoaderContainer}>
        <div className={classes.skeletonHeader}>
          <div className={classes.skeletonText} style={{ width: '220px', height: '30px' }} />
          <div className={classes.skeletonButton} style={{ width: '120px', height: '36px' }} />
        </div>

        <div className={classes.formSection}>
          <div className={classes.formFieldGrid}>
            {[...Array(4)].map((_, i) => (
              <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div className={classes.skeletonText} style={{ width: `${30 + i * 8}%`, height: '14px' }} />
                <div className={classes.skeletonButton} style={{ width: '100%', height: '42px' }} />
              </div>
            ))}
          </div>
          <div className={classes.skeletonText} style={{ width: '28%', height: '14px' }} />
          <div className={classes.skeletonButton} style={{ width: '100%', height: '120px' }} />
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
            <div className={classes.skeletonButton} style={{ width: '96px', height: '36px' }} />
            <div className={classes.skeletonButton} style={{ width: '132px', height: '36px' }} />
          </div>
        </div>

        <div className={classes.loadingText}>{message}</div>
      </div>
    );
  }

  if (variant === 'dashboard') {
    return (
      <div className={classes.pageLoaderContainer}>
        <div className={classes.skeletonHeader}>
          <div className={classes.skeletonText} style={{ width: '210px', height: '32px' }} />
          <div className={classes.skeletonButton} style={{ width: '156px', height: '36px' }} />
        </div>

        <div className={classes.dashboardGrid}>
          {[...Array(4)].map((_, i) => (
            <div key={i} className={classes.dashboardCard}>
              <div className={classes.skeletonText} style={{ width: `${36 + i * 8}%`, height: '14px' }} />
              <div className={classes.skeletonText} style={{ width: `${48 + i * 7}%`, height: '28px' }} />
              <div className={classes.skeletonChip} style={{ width: '72px', height: '20px' }} />
            </div>
          ))}
        </div>

        <div className={classes.dashboardChartRow}>
          <div className={classes.dashboardChartCard}>
            <div className={classes.skeletonText} style={{ width: '42%', height: '16px' }} />
            <div className={classes.skeletonText} style={{ width: '28%', height: '12px' }} />
            <div className={classes.chartBars}>
              {[...Array(6)].map((_, i) => (
                <div
                  key={i}
                  className={classes.chartBar}
                  style={{ height: `${40 + i * 22}px` }}
                />
              ))}
            </div>
          </div>
          <div className={classes.dashboardChartCard}>
            <div className={classes.skeletonText} style={{ width: '55%', height: '16px' }} />
            <div className={classes.skeletonText} style={{ width: '34%', height: '12px' }} />
            <div className={classes.listRowsDense}>
              {[...Array(5)].map((_, i) => (
                <div key={i} className={classes.skeletonListItem}>
                  <div className={classes.skeletonAvatar} />
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <div className={classes.skeletonText} style={{ width: `${52 + i * 7}%`, height: '14px' }} />
                    <div className={classes.skeletonText} style={{ width: `${35 + i * 5}%`, height: '11px' }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className={classes.loadingText}>{message}</div>
      </div>
    );
  }

  if (variant === 'editor') {
    return (
      <div className={classes.pageLoaderContainer}>
        <div className={classes.skeletonHeader}>
          <div className={classes.skeletonText} style={{ width: '240px', height: '30px' }} />
          <div style={{ display: 'flex', gap: 10 }}>
            <div className={classes.skeletonButton} style={{ width: '90px', height: '34px' }} />
            <div className={classes.skeletonButton} style={{ width: '120px', height: '34px' }} />
          </div>
        </div>

        <div className={classes.editorShell}>
          <div className={classes.editorSidebar}>
            {[...Array(7)].map((_, i) => (
              <div key={i} className={classes.skeletonButton} style={{ width: `${72 + (i % 2) * 14}%`, height: '42px' }} />
            ))}
          </div>
          <div className={classes.editorCanvas}>
            <div className={classes.editorNode} style={{ top: 40, left: 36 }}>
              <div className={classes.skeletonText} style={{ width: '58%', height: '14px' }} />
              <div className={classes.skeletonText} style={{ width: '78%', height: '12px' }} />
            </div>
            <div className={classes.editorNode} style={{ top: 180, left: 240 }}>
              <div className={classes.skeletonText} style={{ width: '54%', height: '14px' }} />
              <div className={classes.skeletonText} style={{ width: '74%', height: '12px' }} />
            </div>
            <div className={classes.editorNode} style={{ top: 340, left: 120 }}>
              <div className={classes.skeletonText} style={{ width: '50%', height: '14px' }} />
              <div className={classes.skeletonText} style={{ width: '82%', height: '12px' }} />
            </div>
          </div>
        </div>

        <div className={classes.loadingText}>{message}</div>
      </div>
    );
  }

  // Loader simples para loading crítico
  if (variant === 'simple') {
    return (
      <div className={classes.simpleLoaderContainer}>
        <div className={classes.skeletonHeader}>
          <div
            className={classes.skeletonText}
            style={{
              width: '180px',
              height: '28px',
            }}
          />
          <div
            className={classes.skeletonButton}
            style={{
              width: '96px',
              height: '32px',
            }}
          />
        </div>

        <div className={classes.simpleSkeletonContent}>
          {[...Array(4)].map((_, i) => (
            <div key={i} className={classes.skeletonListItem}>
              <div className={classes.skeletonAvatar} />
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <div
                  className={classes.skeletonText}
                  style={{
                    width: `${55 + Math.random() * 25}%`,
                    height: '15px',
                  }}
                />
                <div
                  className={classes.skeletonText}
                  style={{
                    width: `${35 + Math.random() * 25}%`,
                    height: '11px',
                  }}
                />
              </div>
              <div
                className={classes.skeletonButton}
                style={{
                  width: '72px',
                  height: '22px',
                }}
              />
            </div>
          ))}
        </div>

        <div className={classes.loadingText}>{message}</div>
      </div>
    );
  }

  if (variant === 'content') {
    return (
      <div className={classes.contentOnlyContainer}>
        <div className={classes.skeletonContent}>
          <div className={classes.skeletonToolbar}>
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                className={classes.skeletonButton}
                style={{
                  width: i === 0 ? '140px' : '92px',
                  height: '32px',
                }}
              />
            ))}
          </div>

          <div className={classes.skeletonList}>
            {[...Array(8)].map((_, i) => (
              <div key={i} className={classes.skeletonListItem}>
                <div className={classes.skeletonAvatar} />
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <div
                    className={classes.skeletonText}
                    style={{
                      width: `${60 + Math.random() * 25}%`,
                      height: '16px',
                    }}
                  />
                  <div
                    className={classes.skeletonText}
                    style={{
                      width: `${35 + Math.random() * 30}%`,
                      height: '12px',
                    }}
                  />
                </div>
                <div
                  className={classes.skeletonButton}
                  style={{
                    width: '80px',
                    height: '24px',
                  }}
                />
              </div>
            ))}
          </div>
        </div>

        <div className={classes.loadingText}>{message}</div>
      </div>
    );
  }

  if (variant === 'list') {
    return (
      <div className={classes.contentOnlyContainer}>
        <div className={classes.skeletonContent}>
          <div className={classes.skeletonToolbar}>
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                className={classes.skeletonButton}
                style={{
                  width: i === 0 ? '140px' : '92px',
                  height: '32px',
                }}
              />
            ))}
          </div>

          <div className={classes.listRowsDense}>
            {[...Array(8)].map((_, i) => (
              <div key={i} className={classes.listRowDense}>
                <div className={classes.skeletonAvatar} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <div
                    className={classes.skeletonText}
                    style={{
                      width: `${60 + Math.random() * 18}%`,
                      height: '16px',
                    }}
                  />
                  <div
                    className={classes.skeletonText}
                    style={{
                      width: `${35 + Math.random() * 18}%`,
                      height: '12px',
                    }}
                  />
                </div>
                <div className={classes.skeletonText} style={{ width: `${55 + (i % 3) * 8}%`, height: '14px' }} />
                <div className={classes.skeletonChip} style={{ width: '78px', height: '24px' }} />
                <div className={classes.skeletonButton} style={{ width: '86px', height: '28px' }} />
              </div>
            ))}
          </div>
        </div>

        <div className={classes.loadingText}>{message}</div>
      </div>
    );
  }

  // Loader detalhado que simula a interface real
  return (
    <div className={classes.pageLoaderContainer}>
        {/* Header skeleton */}
        <div className={classes.skeletonHeader}>
          <div
            className={classes.skeletonText}
            style={{
              width: '200px',
              height: '32px',
            }}
          />
          <div
            className={classes.skeletonButton}
            style={{
              width: '120px',
              height: '36px',
            }}
          />
        </div>

        {/* Content area skeleton */}
        <div className={classes.skeletonContent}>
          {/* Toolbar skeleton */}
          <div className={classes.skeletonToolbar}>
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                className={classes.skeletonButton}
                style={{
                  width: '100px',
                  height: '32px',
                }}
              />
            ))}
          </div>

          {/* Table/List content skeleton */}
          <div className={classes.skeletonList}>
            {[...Array(8)].map((_, i) => (
              <div key={i} className={classes.skeletonListItem}>
                <div className={classes.skeletonAvatar} />
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <div
                    className={classes.skeletonText}
                    style={{
                      width: `${60 + Math.random() * 40}%`,
                      height: '16px',
                    }}
                  />
                  <div
                    className={classes.skeletonText}
                    style={{
                      width: `${40 + Math.random() * 30}%`,
                      height: '12px',
                    }}
                  />
                </div>
                <div
                  className={classes.skeletonButton}
                  style={{
                    width: '80px',
                    height: '24px',
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
});

PageContentLoader.displayName = 'PageContentLoader';

export default PageContentLoader;
